/**
 * Права одного пользователя — итог, происхождение и правка в одном месте.
 *
 * Итог приходит с сервера готовым (GET /api/admin/users/:id/effective) и
 * считается теми же функциями, что работают на каждом запросе: экран не
 * может показать одно, а система выдать другое. Здесь — только правка
 * личных правил и ролей; правила самих ролей живут в матрице доступа,
 * и экран прямо говорит, что пришло от роли и где это менять.
 */

import { Icon } from '../components/Icon'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError, startPreview } from '../api'
import type {
  AccessSource,
  AdminRole,
  EffectiveAccess,
  EffectiveReport,
  PermissionAuditEntry,
  ScopeDimension,
  ScopeRuleWithSource,
} from '../types'
import { Badge, Card, CheckChip, Empty, Loading, Notice, Toggle as Switch } from '../components/ui'
import { Segmented } from '../finance/parts'
import { formatDate, formatDateTime, initials, pluralize } from '../format'
import { ScopeRuleForm, describePeriodRule } from './AdminUsers'

type Tone = 'good' | 'bad' | 'warn' | undefined

const message = (caught: unknown, fallback: string) => (caught instanceof ApiError ? caught.message : fallback)
const sources = (items: AccessSource[]) => items.map((item) => item.label).join(', ')

export function AdminUserAccess() {
  const { id = '' } = useParams()
  const [view, setView] = useState<EffectiveAccess | null>(null)
  const [roles, setRoles] = useState<AdminRole[]>([])
  const [dimensions, setDimensions] = useState<ScopeDimension[]>([])
  const [history, setHistory] = useState<PermissionAuditEntry[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const running = useRef(new Set<string>())

  const reload = useCallback(async () => {
    const [effective, rolesResponse, dimensionsResponse, historyResponse] = await Promise.all([
      api.effectiveAccess(id),
      api.adminRoles(),
      api.scopeDimensions(),
      api.userHistory(id),
    ])
    setView(effective)
    setRoles(rolesResponse.roles)
    setDimensions(dimensionsResponse.dimensions)
    setHistory(historyResponse.entries)
  }, [id])

  useEffect(() => {
    setView(null)
    setLoadError(null)
    reload().catch((caught) => setLoadError(message(caught, 'Не удалось загрузить права пользователя')))
  }, [reload])

  /** Любое изменение: вызов, перечитывание итога, сообщение об успехе. */
  const act = async (key: string, action: () => Promise<unknown>, done?: string) => {
    // Одно и то же действие дважды подряд (двойной клик) — один запрос.
    if (running.current.has(key)) return
    running.current.add(key)
    setBusy(key)
    setError(null)
    try {
      await action()
      await reload()
      if (done) setFlash(done)
    } catch (caught) {
      setError(message(caught, 'Не удалось сохранить изменение'))
    } finally {
      running.current.delete(key)
      setBusy(null)
    }
  }

  if (loadError) {
    return (
      <div className="stack" style={{ gap: 14 }}>
        <Notice tone="bad">{loadError}</Notice>
        <Link to="/admin/users" className="back-link"><Icon name="arrow-left" size={14} /> Все пользователи</Link>
      </div>
    )
  }
  if (!view) return <Loading rows={8} />

  const { user } = view
  const blocked = Boolean(user.blocked_at)

  return (
    <div className="stack" style={{ gap: 18 }}>
      <Link to="/admin/users" className="small">
        <Icon name="arrow-left" size={14} /> Все пользователи
      </Link>

      {flash && (
        <Notice tone="info">
          <span>{flash}</span>{' '}
          <button className="btn btn--ghost btn--sm" onClick={() => setFlash(null)}>
            Скрыть
          </button>
        </Notice>
      )}
      {error && <Notice tone="bad">{error}</Notice>}

      <Card>
        <div className="access-head">
          <span className="avatar" style={{ width: 40, height: 40, fontSize: 'var(--text-md)' }}>
            {initials(user.full_name)}
          </span>
          <div className="access-head__who">
            <div className="access-head__name">{user.full_name}</div>
            <div className="small secondary">
              {user.position ? `${user.position} · ` : ''}
              {user.email}
            </div>
            <div className="row row--tight" style={{ marginTop: 6 }}>
              {user.is_admin && <Badge tone="accent">Администратор</Badge>}
              {blocked ? <Badge tone="bad">заблокирован</Badge> : <Badge tone="good">активен</Badge>}
              <span className="tiny muted">
                Последний вход: {user.last_login_at ? formatDateTime(user.last_login_at) : 'не входил'}
              </span>
            </div>
          </div>
          <div className="stack" style={{ gap: 4, alignItems: 'flex-start' }}>
            <button
              className="btn btn--primary"
              disabled={blocked || user.is_admin}
              onClick={() => {
                startPreview(user.id)
                window.location.assign('/')
              }}
            >
              Посмотреть как этот пользователь
            </button>
            <span className="tiny muted" style={{ maxWidth: 260 }}>
              {blocked
                ? 'Заблокированный ничего не видит — смотреть нечего.'
                : user.is_admin
                  ? 'Администратор видит то же, что и вы.'
                  : user.can_monitor_ingest
                    ? 'Откроется система с его меню, отчётами и срезом, включая кабинет загрузки — ровно как у него. Изменить ничего нельзя.'
                    : 'Откроется система с его меню, отчётами и срезом. Изменить ничего нельзя.'}
            </span>
          </div>
        </div>

        {blocked && (
          <div style={{ marginTop: 14 }}>
            <Notice tone="bad">
              Доступ заблокирован{user.blocked_reason ? `: ${user.blocked_reason}` : ''}. Ниже — что он увидит после
              разблокировки.
            </Notice>
          </div>
        )}
        {user.is_admin && (
          <div style={{ marginTop: 14 }}>
            <Notice tone="info">
              Администратор видит всё и настраивает права: роли, скрытия и срез на него не действуют. Чтобы
              ограничить человека, сначала снимите права администратора.
            </Notice>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Summary view={view} />
        </div>
      </Card>

      <Card title="Роли и особые права">
        <div className="stack" style={{ gap: 14 }}>
          <div>
            <div className="field__label" style={{ marginBottom: 8 }}>
              Роли — дают отчёты и правила, общие для должности. Роли складываются.
            </div>
            <div className="chip-group">
              {roles.map((role) => {
                const on = view.roles.some((item) => item.id === role.id)
                return (
                  <CheckChip
                    key={role.id}
                    checked={on}
                    disabled={busy === 'roles'}
                    title={role.description ?? undefined}
                    onClick={() =>
                      act(
                        'roles',
                        () =>
                          api.setUserRoles(
                            user.id,
                            on ? view.roles.filter((item) => item.id !== role.id).map((item) => item.id) : [...view.roles.map((item) => item.id), role.id],
                          ),
                        on ? `Роль «${role.title}» снята` : `Роль «${role.title}» назначена`,
                      )
                    }
                  >
                    {role.title}
                  </CheckChip>
                )
              })}
            </div>
            <div className="tiny muted" style={{ marginTop: 6 }}>
              Что входит в роль, правится в <Link to="/admin/access">матрице доступа</Link>.
            </div>
          </div>

          <Toggle
            label="Кабинет загрузки данных"
            hint="Видит, что прислала 1С и что разобралось, и итоги по месяцам всей компании — выручку, себестоимость, валовую прибыль — для сверки с 1С. Отчётов в разрезах не видит. Для подрядчика 1С."
            on={user.can_monitor_ingest || user.is_admin}
            disabled={user.is_admin || busy === 'ingest'}
            onChange={(next) =>
              act('ingest', () => api.setIngestMonitor(user.id, next), next ? 'Кабинет загрузки открыт' : 'Кабинет загрузки закрыт')
            }
          />
          <Toggle
            label="Администратор"
            hint="Видит всё, включая эту страницу, и меняет права других."
            on={user.is_admin}
            disabled={busy === 'admin'}
            onChange={(next) => {
              if (next && !confirm(`Сделать ${user.full_name} администратором? Он увидит все данные и сможет менять права.`)) return
              void act('admin', () => api.setAdmin(user.id, next), next ? 'Назначен администратором' : 'Права администратора сняты')
            }}
          />
        </div>
      </Card>

      <MenuCard view={view} busy={busy} act={act} />

      <ReportsCard view={view} busy={busy} act={act} />

      <Card title="Срез данных">
        <div className="stack" style={{ gap: 14 }}>
          {view.scope.length === 0 ? (
            <div className="small secondary">Ограничений нет: в доступных отчётах видны данные всех городов, направлений и организаций.</div>
          ) : (
            <div className="access-list">
              {view.scope.map((item) => (
                <div className="access-row" key={item.dimension}>
                  <div className="access-row__main">
                    <div className="access-row__title">{item.title}</div>
                    <div className="access-row__note">
                      {item.allow !== null && <>Только: {item.allow.join(', ') || 'ничего'}. </>}
                      {item.deny.length > 0 && <>Кроме: {item.deny.join(', ')}.</>}
                    </div>
                    {item.closes_reports.length > 0 && (
                      <div className="access-row__note access-row__note--warn">
                        Поэтому закрыты целиком (не делятся по этому признаку): {item.closes_reports.join(', ')}.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <RuleList
            rules={view.scope_rules}
            empty="Правил среза нет."
            describe={(rule) => `${rule.mode === 'allow' ? 'только' : 'кроме'} ${rule.labels.join(', ') || rule.values.join(', ')}`}
            busy={busy}
            onDelete={(rule) => act(`rule-${rule.id}`, () => api.deleteScopeRule(rule.id), 'Правило снято')}
          />

          <div>
            <div className="field__label" style={{ marginBottom: 8 }}>
              Добавить личное ограничение
            </div>
            <ScopeRuleForm
              userId={user.id}
              dimensions={dimensions}
              onCreated={async () => {
                await reload()
                setFlash('Правило добавлено и действует сразу: права считаются на каждый запрос.')
              }}
              onError={setError}
            />
            <div className="tiny muted" style={{ marginTop: 6 }}>
              Если отчёт нельзя ограничить выбранным признаком, он закроется целиком, а не покажет все данные.
            </div>
          </div>
        </div>
      </Card>

      <Card title="Глубина истории">
        <div className="stack" style={{ gap: 12 }}>
          <div className="small">
            {view.period.not_before
              ? `Данные доступны с ${formatDate(view.period.not_before)}.`
              : view.period.closed_only
                ? 'Доступна вся история, но только закрытые в 1С месяцы.'
                : 'Вся история без ограничений.'}
            {view.period.not_before && view.period.closed_only && ' Только закрытые в 1С месяцы.'}
          </div>
          <RuleList
            rules={view.period.rules}
            empty="Ограничений по периоду нет. Добавить — в форме «Срез данных», измерение «Период»."
            describe={(rule) => describePeriodRule(rule)}
            busy={busy}
            onDelete={(rule) => act(`rule-${rule.id}`, () => api.deleteScopeRule(rule.id), 'Ограничение по периоду снято')}
          />
          {view.period.rules.length > 1 && (
            <div className="tiny muted">Если правил несколько, действует самое строгое.</div>
          )}
        </div>
      </Card>

      <Card title="Учётная запись">
        <div className="row">
          <button
            className="btn btn--sm"
            disabled={busy === 'password'}
            onClick={async () => {
              setBusy('password')
              try {
                const result = await api.resetPassword(user.id)
                setFlash(`Новый пароль для ${user.full_name}: ${result.password} — он показан один раз, передайте его лично.`)
                await reload()
              } catch (caught) {
                setError(message(caught, 'Не удалось сбросить пароль'))
              } finally {
                setBusy(null)
              }
            }}
          >
            Сбросить пароль
          </button>
          <button
            className="btn btn--sm"
            disabled={busy === 'sessions'}
            onClick={() => act('sessions', () => api.revokeSessions(user.id), 'Все сессии завершены: войти придётся заново')}
          >
            Завершить все сессии
          </button>
          {blocked ? (
            <button className="btn btn--sm" disabled={busy === 'block'} onClick={() => act('block', () => api.unblockUser(user.id), 'Доступ восстановлен')}>
              Разблокировать
            </button>
          ) : (
            <button
              className="btn btn--sm btn--danger"
              disabled={busy === 'block'}
              onClick={() => {
                const reason = prompt(`Причина блокировки для «${user.full_name}»`, 'Уволен')
                if (reason === null) return
                void act('block', () => api.blockUser(user.id, reason), 'Доступ снят, сессии завершены')
              }}
            >
              Заблокировать
            </button>
          )}
        </div>
      </Card>

      <Card title="История изменений прав">
        {history === null ? (
          <Loading rows={3} />
        ) : history.length === 0 ? (
          <Empty title="Изменений пока не было" hint="Сюда попадает каждое изменение прав этого человека: кто и что поменял." />
        ) : (
          <div className="access-list">
            {history.map((entry) => (
              <div className="access-row" key={entry.id}>
                <div className="access-row__main">
                  <div className="small">{entry.summary}</div>
                  <div className="access-row__note">
                    {formatDateTime(entry.at)} · {entry.actor_name ?? 'система'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="tiny muted" style={{ marginTop: 8 }}>
          Изменения ролей, которые затронули человека, видны в <Link to="/admin/log?tab=changes">общем журнале</Link>.
        </div>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------

function Summary({ view }: { view: EffectiveAccess }) {
  const visibleSections = view.menu.flatMap((group) => group.sections.filter((section) => section.visible))
  const groups = view.menu.filter((group) => group.sections.some((section) => section.visible)).map((group) => group.title)
  const hiddenMetrics = view.reports
    .filter((report) => report.status === 'allowed')
    .reduce((count, report) => count + report.metrics.filter((metric) => !metric.visible).length, 0)

  const items: Array<{ label: string; value: ReactNode }> = [
    { label: 'Разделы меню', value: groups.length > 0 ? `${groups.join(', ')} · ${visibleSections.length} экр.` : 'нет' },
    { label: 'Дополнительные отчёты', value: view.extra_reports > 0 ? pluralize(view.extra_reports, 'отчёт', 'отчёта', 'отчётов') : 'нет' },
    {
      label: 'Срез данных',
      value:
        view.scope.length === 0
          ? 'все данные'
          : view.scope.map((item) => `${item.title}: ${item.allow ? item.allow.join(', ') : `кроме ${item.deny.join(', ')}`}`).join('; '),
    },
    {
      label: 'Глубина истории',
      value: view.period.not_before ? `с ${formatDate(view.period.not_before)}` : view.period.closed_only ? 'только закрытые месяцы' : 'вся история',
    },
    { label: 'Скрыто показателей', value: hiddenMetrics > 0 ? String(hiddenMetrics) : 'нет' },
  ]

  return (
    <div className="access-summary">
      {items.map((item) => (
        <div className="access-summary__item" key={item.label}>
          <div className="access-summary__label">{item.label}</div>
          <div className="access-summary__value">{item.value}</div>
        </div>
      ))}
    </div>
  )
}

function Toggle({
  label,
  hint,
  on,
  disabled,
  onChange,
}: {
  label: string
  hint: string
  on: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
}) {
  return <Switch checked={on} onChange={onChange} disabled={disabled} label={label} hint={hint} />
}

type Act = (key: string, action: () => Promise<unknown>, done?: string) => Promise<void>

function MenuCard({ view, busy, act }: { view: EffectiveAccess; busy: string | null; act: Act }) {
  const userId = view.user.id
  const hide = (code: string, hidden: boolean, title: string) =>
    act(
      `section-${code}`,
      () => api.setSectionHidden({ subject_type: 'user', subject_id: userId, section_code: code, hidden }),
      hidden ? `«${title}» убран из меню` : `«${title}» снова в меню (если выданы отчёты)`,
    )

  return (
    <Card title="Главное меню">
      <div className="stack" style={{ gap: 4 }}>
        <div className="small secondary" style={{ marginBottom: 8 }}>
          Раздел появляется, когда выдан отчёт, на котором он построен. Здесь его можно скрыть лично. Скрытие убирает
          экран, а не цифры: данные закрываются в списке отчётов ниже.
        </div>

        {view.menu.map((group) => {
          const groupPersonal = group.hidden_personally
          const groupByRole = group.hidden_by.filter((source) => source.type === 'role')
          return (
            <div className="access-group" key={group.code}>
              <div className="access-group__head">
                <span className="access-group__title">{group.title}</span>
                {groupByRole.length > 0 && <Badge tone="warn">скрыт: {sources(groupByRole)}</Badge>}
                <button
                  className="btn btn--sm btn--ghost"
                  disabled={busy === `section-${group.code}` || view.user.is_admin}
                  onClick={() => hide(group.code, !groupPersonal, group.title)}
                >
                  {groupPersonal ? 'Вернуть раздел' : 'Скрыть весь раздел'}
                </button>
              </div>
              <div className="access-list">
                {group.sections.map((section) => {
                  const byRole = section.hidden_by.filter((source) => source.type === 'role')
                  return (
                    <div className="access-row" key={section.code}>
                      <span className={`access-dot${section.visible ? ' access-dot--on' : ''}`} aria-hidden="true" />
                      <div className="access-row__main">
                        <div className="access-row__title">
                          {section.title}{' '}
                          <span className="sr-only">{section.visible ? '— виден' : '— не виден'}</span>
                        </div>
                        <div className="access-row__note">
                          {section.visible
                            ? `Виден. Построен на: ${section.reports.join(', ')}`
                            : section.hidden
                              ? section.hidden_by.length > 0
                                ? `Скрыт: ${sources(section.hidden_by)}`
                                : `Скрыт вместе с разделом «${group.title}»`
                              : `Не виден: ${section.reason}`}
                        </div>
                        {section.data_still_open_in.length > 0 && (
                          <div className="access-row__note access-row__note--warn">
                            Цифры этого экрана по-прежнему доступны в «Дополнительных отчётах»: {section.data_still_open_in.join(', ')}.
                          </div>
                        )}
                        {byRole.length > 0 && !section.hidden_personally && (
                          <div className="tiny muted">Скрытие ролью снимается в матрице доступа.</div>
                        )}
                      </div>
                      <div className="access-row__side">
                        <button
                          className="btn btn--sm btn--ghost"
                          disabled={busy === `section-${section.code}` || view.user.is_admin}
                          onClick={() => hide(section.code, !section.hidden_personally, section.title)}
                        >
                          {section.hidden_personally ? 'Вернуть' : 'Скрыть'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}

        <div className="access-group">
          <div className="access-group__head">
            <span className="access-group__title">Всегда в меню</span>
          </div>
          <div className="access-list">
            <FixedItem on title="Краткая сводка" note="Плитки по выданным отчётам" />
            <FixedItem
              on={view.extra_reports > 0}
              title="Дополнительные отчёты"
              note={view.extra_reports > 0 ? pluralize(view.extra_reports, 'отчёт', 'отчёта', 'отчётов') : 'Пусто: отчёты не выданы'}
            />
            <FixedItem
              on={view.user.can_monitor_ingest || view.user.is_admin}
              title="Загрузка данных"
              note="Включается переключателем «Кабинет загрузки данных»"
            />
            <FixedItem on={view.user.is_admin} title="Администрирование" note="Только у администратора" />
          </div>
        </div>
      </div>
    </Card>
  )
}

function FixedItem({ on, title, note }: { on: boolean; title: string; note: string }) {
  return (
    <div className="access-row">
      <span className={`access-dot${on ? ' access-dot--on' : ''}`} aria-hidden="true" />
      <div className="access-row__main">
        <div className="access-row__title">
          {title} <span className="sr-only">{on ? '— виден' : '— не виден'}</span>
        </div>
        <div className="access-row__note">{note}</div>
      </div>
    </div>
  )
}

const STATUS: Record<EffectiveReport['status'], { text: string; tone: Tone }> = {
  allowed: { text: 'открыт', tone: 'good' },
  denied: { text: 'запрещён', tone: 'bad' },
  none: { text: 'не выдан', tone: undefined },
  unavailable: { text: 'не откроется', tone: 'warn' },
}

function ReportsCard({ view, busy, act }: { view: EffectiveAccess; busy: string | null; act: Act }) {
  const [filter, setFilter] = useState<'open' | 'all'>('open')
  const [expanded, setExpanded] = useState<string | null>(null)
  const userId = view.user.id
  const list = filter === 'open' ? view.reports.filter((report) => report.status !== 'none') : view.reports

  const setRule = (report: EffectiveReport, mode: 'allow' | 'deny' | null) =>
    act(
      `report-${report.code}`,
      () => api.setReportAccess({ subject_type: 'user', subject_id: userId, report_code: report.code, mode }),
      mode === 'allow'
        ? `«${report.title}» выдан лично`
        : mode === 'deny'
          ? `«${report.title}» запрещён лично — сильнее любых ролей`
          : `Личное правило для «${report.title}» снято: как у роли`,
    )

  return (
    <Card
      title="Отчёты и показатели"
      actions={
        <Segmented
          size="sm"
          label="Какие отчёты"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'open', label: `С правилами · ${view.reports.filter((report) => report.status !== 'none').length}` },
            { value: 'all', label: `Все · ${view.reports.length}` },
          ]}
        />
      }
    >
      <div className="small secondary" style={{ marginBottom: 8 }}>
        Открытые отчёты попадают в «Дополнительные отчёты» и питают разделы меню. Без правила отчёт закрыт. Личный
        запрет сильнее разрешений всех ролей.
      </div>

      {list.length === 0 ? (
        <Empty title="Ни один отчёт не выдан" hint="Назначьте роль выше или откройте отчёт лично в списке «Все»." />
      ) : (
        <div className="access-list">
          {list.map((report) => {
            const status = STATUS[report.status]
            const personalMode = report.user_rule?.mode ?? null
            const fromRoles = report.role_rules
              .map((rule) => `${rule.mode === 'allow' ? 'выдан' : 'запрещён'}: ${rule.source.label}`)
              .join('; ')
            const open = expanded === report.code
            const hiddenCount = report.metrics.filter((metric) => !metric.visible).length

            return (
              <div className="access-row" key={report.code}>
                <div className="access-row__main">
                  <div className="access-row__title">
                    {report.title} <Badge tone={status.tone}>{status.text}</Badge>
                  </div>
                  <div className="access-row__note">
                    {report.category}
                    {fromRoles ? ` · ${fromRoles}` : ''}
                    {personalMode ? ` · личное правило: ${personalMode === 'allow' ? 'выдан' : 'запрещён'}` : ''}
                  </div>
                  {report.reason && report.status !== 'allowed' && <div className="access-row__note">{report.reason}</div>}
                  {report.status === 'allowed' && (
                    <button className="btn btn--ghost btn--sm" style={{ paddingLeft: 0 }} onClick={() => setExpanded(open ? null : report.code)} aria-expanded={open}>
                      {open ? 'Свернуть показатели' : `Показатели${hiddenCount > 0 ? ` · скрыто ${hiddenCount}` : ''}`}
                    </button>
                  )}
                  {report.leaks.map((leak) => (
                    <div className="access-row__note access-row__note--warn" key={leak}>
                      {leak}
                    </div>
                  ))}
                  {open && <MetricChips report={report} userId={userId} busy={busy} act={act} />}
                </div>
                <div className="access-row__side">
                  <Segmented
                    size="sm"
                    label={`Личное правило: ${report.title}`}
                    value={personalMode ?? 'role'}
                    onChange={(next) => {
                      if (busy === `report-${report.code}` || view.user.is_admin) return
                      void setRule(report, next === 'role' ? null : next)
                    }}
                    options={[
                      { value: 'role', label: 'Как у роли', disabled: view.user.is_admin },
                      { value: 'allow', label: 'Выдать', disabled: view.user.is_admin },
                      { value: 'deny', label: 'Запретить', disabled: view.user.is_admin },
                    ]}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

function MetricChips({ report, userId, busy, act }: { report: EffectiveReport; userId: string; busy: string | null; act: Act }) {
  return (
    <div>
      <div className="tiny muted" style={{ marginTop: 8 }}>
        Нажмите на показатель, чтобы скрыть его лично. Скрытый не приходит ни в таблице, ни в выгрузке, ни в разделах меню.
      </div>
      <div className="metric-chips">
        {report.metrics.map((metric) => {
          const byRole = metric.hidden_by.filter((source) => source.type === 'role')
          const lockedByRole = !metric.visible && !metric.hidden_personally
          const key = `metric-${report.code}-${metric.key}`
          return (
            <button
              key={metric.key}
              type="button"
              className={`metric-chip${metric.visible ? '' : ' metric-chip--hidden'}`}
              aria-pressed={!metric.visible}
              disabled={busy === key || lockedByRole}
              title={
                lockedByRole
                  ? `${metric.reason ?? 'Скрыт'}. Снимается в матрице доступа.`
                  : metric.visible
                    ? 'Виден. Нажмите, чтобы скрыть'
                    : 'Скрыт лично. Нажмите, чтобы показать'
              }
              onClick={() =>
                act(
                  key,
                  () =>
                    api.setMetricHidden({
                      subject_type: 'user',
                      subject_id: userId,
                      report_code: report.code,
                      metric: metric.key,
                      hidden: metric.visible,
                    }),
                  metric.visible
                    ? `«${metric.title}» скрыт в отчёте «${report.title}»`
                    : byRole.length > 0
                      ? `Личное скрытие «${metric.title}» снято, но показатель по-прежнему скрыт: ${sources(byRole)}`
                      : `«${metric.title}» снова виден`,
                )
              }
            >
              <Icon name={metric.visible ? 'eye' : 'x'} size={12} className="metric-chip__mark" />
              {metric.title}
              {byRole.length > 0 && <span className="tiny muted">· {sources(byRole)}</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function RuleList({
  rules,
  empty,
  describe,
  busy,
  onDelete,
}: {
  rules: ScopeRuleWithSource[]
  empty: string
  describe: (rule: ScopeRuleWithSource) => string
  busy: string | null
  onDelete: (rule: ScopeRuleWithSource) => void
}) {
  if (rules.length === 0) return <div className="tiny muted">{empty}</div>

  return (
    <div className="access-list">
      {rules.map((rule) => (
        <div className="access-row" key={rule.id}>
          <div className="access-row__main">
            <div className="access-row__title">
              {rule.dimension_title}: <span style={{ fontWeight: 400 }}>{describe(rule)}</span>
            </div>
            <div className="access-row__note">
              {rule.source.type === 'user' ? 'Личное правило' : `От роли: ${rule.source.label} — правится в матрице доступа`}
              {rule.comment ? ` · ${rule.comment}` : ''}
            </div>
          </div>
          {rule.source.type === 'user' && (
            <div className="access-row__side">
              <button className="btn btn--sm btn--ghost btn--danger" disabled={busy === `rule-${rule.id}`} onClick={() => onDelete(rule)}>
                Снять
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
